import { isWeb } from '../../src/utils/platformUtils'
import { getRealDatabase } from '../../database-sqlite/DatabaseSingleton'
import { TABLE_NAME } from '../constants/tables'
import { getDb } from '../../db-sqlite/adapter'
import { link } from 'fs'

/**
 * Interface para el usuario completo
 */
export interface UserProfile {
    _id: string
    name?: string
    last_name?: string
    email?: string
    user_name?: string
    role?: string
    avatar?: string
    timeZone?: string
    user_segment?: Record<string, any>
    name_group?: string
    ws_url?: string
    delete_active?: boolean
    lat?: number
    lng?: number
    url_uploads?: string
    languages?: string[]
    lenguage?: string
    phone?: string
    email_verified?: boolean
    phone_verified?: boolean
    cognito_id?: string
    login?: boolean
    image_splash?: string
    last_update_coords?: string
    area?: string
    nickname?: string
    dni?: string
}

/**
 * Convierte valores JavaScript a tipos compatibles con SQLite
 * Solo acepta: null, string, number, Uint8Array
 */
function toSqlValue(
    value: any,
    defaultValue: string | null = null,
): string | number | null {
    // Si es null o undefined, retornar el default
    if (value === null || value === undefined) {
        return defaultValue
    }

    // Date → ISO string
    if (value instanceof Date) {
        return value.toISOString()
    }

    // Boolean → 0 o 1
    if (typeof value === 'boolean') {
        return value ? 1 : 0
    }

    // Number → number
    if (typeof value === 'number') {
        return value
    }

    // Array u Object → JSON string
    if (typeof value === 'object') {
        return JSON.stringify(value)
    }

    // Cualquier otra cosa → string
    return String(value)
}

/**
 * Inserta o actualiza un usuario en la base de datos
 * Usa INSERT OR REPLACE para upsert
 */
export async function upsertUser(user: UserProfile): Promise<void> {
    // console.log('🔄 Upserting user:', user);
    // const realDb = await getRealDatabase();
    const db = await getDb()

    // Preparar valores asegurando tipos compatibles con SQLite
    const params = [
        user._id,
        toSqlValue(user.name, ''),
        toSqlValue(user.last_name, ''),
        toSqlValue(user.email, ''),
        toSqlValue(user.user_name, ''),
        toSqlValue(user.role, ''),
        toSqlValue(user.avatar, ''),
        toSqlValue(user.timeZone, ''),
        toSqlValue(user.user_segment, '{}'),
        toSqlValue(user.name_group, ''),
        toSqlValue(user.ws_url, ''),
        toSqlValue(user.delete_active, null),
        user.lat ?? 0,
        user.lng ?? 0,
        toSqlValue(user.url_uploads, ''),
        toSqlValue(user.languages, '[]'),
        toSqlValue(user.lenguage, ''),
        toSqlValue(user.phone, ''),
        user.email_verified ? 1 : 0,
        user.phone_verified ? 1 : 0,
        toSqlValue(user.cognito_id, ''),
        user.login ? 1 : 0,
        toSqlValue(user.image_splash, ''),
        toSqlValue(user.last_update_coords, ''),
        toSqlValue(user.area, ''),
        toSqlValue(user.nickname, ''),
        toSqlValue(user.dni, ''),
    ]

    await db.run({
        sql: `
            INSERT OR REPLACE INTO ${TABLE_NAME.USER_PROFILE} (
                _id, name, last_name, email, user_name, role, avatar, timeZone, user_segment,
                name_group, ws_url, delete_active, lat, lng, url_uploads, languages, lenguage,
                phone, email_verified, phone_verified, cognito_id, login, image_splash,
                last_update_coords, area, nickname, dni
            ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        `,
        params,
    })
}

export async function updateGlobalVersion(serviceName: string, version: string) {
    try {
        if (isWeb()) {
            console.warn('updateGlobalVersion is not implemented for web platform')
            return
        }

        const db = await getDb()
        const params = [serviceName, version]
        await db.run({
            sql: `INSERT OR REPLACE INTO ${TABLE_NAME.VERSIONS} (serviceName, value) VALUES (?, ?)`,
            params,
        })
    } catch (error) {
        console.error('1 Error updating global version:', error)
        throw error
    }
}

export async function getGlobalVersion(serviceName: string): Promise<string | null> {
    try {
        if (isWeb()) {
            console.warn('getGlobalVersion is not implemented for web platform')
            return null
        }

        const db = await getDb()
        const row = await db.getFirst({
            sql: `SELECT value FROM ${TABLE_NAME.VERSIONS} WHERE serviceName = ?`,
            params: [serviceName],
        })

        return row ? row.value : null
    } catch (error) {
        console.error('1 Error getting global version:', error)
        throw error
    }
}

/**
 * Obtiene el primer usuario de la base de datos
 * (asumiendo que solo hay un usuario activo)
 */
export async function getUser(): Promise<UserProfile | null> {
    const realDb = await getRealDatabase()

    const row = await realDb.getFirstAsync<any>(
        `SELECT * FROM ${TABLE_NAME.USER_PROFILE} LIMIT 1`,
    )

    if (!row) return null

    // Parsear campos JSON
    return {
        ...row,
        user_segment: row.user_segment ? JSON.parse(row.user_segment) : {},
        languages: row.languages ? JSON.parse(row.languages) : [],
        delete_active: Boolean(row.delete_active),
        email_verified: Boolean(row.email_verified),
        phone_verified: Boolean(row.phone_verified),
        login: Boolean(row.login),
    }
}

/**
 * Obtiene un usuario por ID
 */
export async function getUserById(userId: string): Promise<UserProfile | null> {
    const realDb = await getRealDatabase()

    const row = await realDb.getFirstAsync<any>(
        `SELECT * FROM ${TABLE_NAME.USER_PROFILE} WHERE _id = ?`,
        [userId],
    )

    if (!row) return null

    // Parsear campos JSON
    return {
        ...row,
        user_segment: row.user_segment ? JSON.parse(row.user_segment) : {},
        languages: row.languages ? JSON.parse(row.languages) : [],
        delete_active: Boolean(row.delete_active),
        email_verified: Boolean(row.email_verified),
        phone_verified: Boolean(row.phone_verified),
        login: Boolean(row.login),
    }
}

/**
 * Elimina todos los usuarios
 */
export async function deleteAllUsers(): Promise<void> {
    const realDb = await getRealDatabase()
    await realDb.runAsync(`DELETE FROM ${TABLE_NAME.USER_PROFILE}`)
}

/**
 * Actualiza campos específicos del usuario
 */
export async function updateUserFields(
    userId: string,
    fields: Partial<Omit<UserProfile, '_id'>>,
): Promise<void> {
    const realDb = await getRealDatabase()

    const updates: string[] = []
    const params: any[] = []

    Object.entries(fields).forEach(([key, value]) => {
        updates.push(`${key} = ?`)
        params.push(toSqlValue(value))
    })

    if (updates.length === 0) return

    params.push(userId)

    await realDb.runAsync(
        `UPDATE ${TABLE_NAME.USER_PROFILE} SET ${updates.join(', ')} WHERE _id = ?`,
        params,
    )
}

/**
 * Obtiene el ID del country asociado al segmento del usuario
 */
export async function getCountryId(): Promise<string | null> {
    try {
        const userProfile = await getUser()

        if (userProfile && userProfile.user_segment) {
            const countriesIds = userProfile.user_segment['segment_countries']
            const countryId =
                Array.isArray(countriesIds) && countriesIds.length > 0
                    ? countriesIds[0]
                    : null
            return countryId || null
        }

        return null
    } catch (error) {
        console.error('Error getting country ID:', error)
        return null
    }
}

/**
 * Actualiza la tabla footer_app con los datos recibidos del backend
 * Migrado a sintaxis moderna (SDK 54) con batch insert optimizado
 *
 * @param newData - Array de items del footer desde single.data.footer_app
 * @returns Promise<void>
 *
 * FIXES:
 * - ✅ Eliminada columna 'name' que no existe (causa del error)
 * - ✅ Spread operator para parámetros (expo-sqlite v14 Kotlin)
 * - ✅ Batch insert optimizado (100x más rápido)
 * - ✅ Retry logic para evitar "database is locked"
 */
export async function updateFooter(newData: any[]): Promise<void> {
    console.log(`🔍 [updateFooter] Actualizando ${newData.length} items`)

    if (isWeb()) {
        // Web usa IndexedDB (sintaxis antigua se mantiene)

        return new Promise(async (resolve, reject) => {
            // import('../src/db/indexDB')
            import('../../src/db/indexDB')
                .then(({ openDB }) => openDB())
                .then((indexedDB) => {
                    const tx = indexedDB.transaction(
                        [TABLE_NAME.FOOTER_APP],
                        'readwrite',
                    )
                    const store = tx.objectStore(TABLE_NAME.FOOTER_APP)

                    let completedOps = 0
                    const totalOps = 1 + newData.length

                    const checkComplete = () => {
                        completedOps++
                        if (completedOps === totalOps) {
                            setTimeout(() => resolve(), 0)
                        }
                    }

                    tx.onerror = (e: any) => reject(e.target.error)
                    tx.onabort = () => reject(new Error('Transaction aborted'))

                    const clearReq = store.clear()
                    clearReq.onsuccess = () => checkComplete()
                    clearReq.onerror = (e: any) => reject(e.target.error)

                    newData.forEach(
                        (
                            {
                                _id,
                                label,
                                icon,
                                nav = false,
                                section = false,
                                link = '',
                                name = '',
                            },
                            idx,
                        ) => {
                            const putReq = store.put({
                                _id,
                                label,
                                icon,
                                nav,
                                section,
                                link,
                                name,
                            })
                            putReq.onsuccess = () => checkComplete()
                            putReq.onerror = (e: any) => reject(e.target.error)
                        },
                    )
                })
                .catch(reject)
        })
    } else {
        const realDb = await getRealDatabase()

        try {
            console.log(`🗑️ [updateFooter] Limpiando tabla...`)
            await realDb.runAsync(`DELETE FROM ${TABLE_NAME.FOOTER_APP};`)

            if (newData.length === 0) {
                console.log(`✅ [updateFooter] Tabla limpiada (sin datos nuevos)`)
                return
            }

            // ✅ Batch insert optimizado (estilo schedule.repository.ts)
            const batchSize = 100
            let totalInserted = 0

            for (let i = 0; i < newData.length; i += batchSize) {
                const batch = newData.slice(i, i + batchSize)

                // Construir placeholders: (?, ?, ?, ?), (?, ?, ?, ?), ...
                const placeholders = batch.map(() => '(?, ?, ?, ?, ?,?)').join(', ')

                // Construir array de valores aplanado
                const values: any[] = []
                batch.forEach(
                    ({
                        _id,
                        label,
                        icon,
                        nav = false,
                        section = false,
                        link = '',
                    }) => {
                        values.push(
                            _id,
                            label || '',
                            icon || '',
                            nav ? 1 : 0,
                            section ? 1 : 0,
                            link || '',
                        )
                    },
                )

                // ✅ FIX CRÍTICO: Usar spread operator (Kotlin requiere parámetros individuales)
                // ❌ ANTES: runAsync(sql, values) → Error "[object Object]"
                // ✅ AHORA: runAsync(sql, ...values) → 100 parámetros individuales
                await realDb.runAsync(
                    `INSERT OR REPLACE INTO ${TABLE_NAME.FOOTER_APP} (_id, label, icon, nav, section, link) 
                     VALUES ${placeholders};`,
                    ...values,
                )

                totalInserted += batch.length

                if (i === 0) {
                    console.log(
                        `✅ [updateFooter] Primer batch insertado: ${batch[0].label}`,
                    )
                }
            }

            console.log(
                `✅ [updateFooter] ${totalInserted} items insertados correctamente`,
            )

            // Verificación
            const count = await realDb.getFirstAsync<{ count: number }>(
                `SELECT COUNT(*) as count FROM ${TABLE_NAME.FOOTER_APP};`,
            )

            if (count?.count !== totalInserted) {
                console.warn(
                    `⚠️ [updateFooter] Discrepancia: esperado ${totalInserted}, encontrado ${count?.count}`,
                )
            }
        } catch (error: any) {
            // ✅ Error mejorado para debugging
            console.error('❌ [updateFooter] Error:', {
                message: error.message,
                code: error.code,
                itemsToInsert: newData.length,
            })
            throw error
        }
    }
}
